import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { RefObject } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreePrivacy) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      const response = await fetch('https://readdy.ai/api/form/d6n807g5dsf7sr1n6k10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setAgreePrivacy(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 lg:py-20 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="hidden lg:block lg:col-span-2"></div>

          {/* Content */}
          <div
            ref={contentRef as RefObject<HTMLDivElement>}
            className={`lg:col-span-4 space-y-6 transition-all duration-700 ${
              contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-white/60 border border-slate-900/10 text-slate-900/70 text-sm font-semibold uppercase tracking-widest rounded">
              Let's Build Your Success Together
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight">
              Ready to<br />Take the<br />Next Step?
            </h2>
            <p className="text-lg text-slate-900 leading-relaxed">
              Whether you're launching, expanding, or streamlining your operations, our experts are here to guide you with tailored solutions.
            </p>
            <p className="text-lg">
              <span className="font-semibold text-slate-900">Partner with CJ Solutions</span>
              <span className="text-slate-900"> and unlock strategic growth across regulatory, financial, and corporate services.</span>
            </p>
          </div>

          {/* Form */}
          <div
            ref={formRef as RefObject<HTMLDivElement>}
            className={`lg:col-span-5 w-full transition-all duration-700 delay-200 ${
              formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
              <h3 className="text-2xl lg:text-3xl font-semibold text-slate-900 mb-6 lg:mb-8">
                Get Expert Advice Today
              </h3>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Name and surname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" id="name" name="name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required placeholder="Enter Your Name"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email" id="email" name="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required placeholder="Email Address"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel" id="phone" name="phone" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required placeholder="Phone Number"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message" name="message" value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    maxLength={500} rows={4} placeholder="Add Your Message"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none text-sm"
                  ></textarea>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox" id="privacy" checked={agreePrivacy}
                    onChange={(e) => setAgreePrivacy(e.target.checked)} required
                    className="mt-1 w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="privacy" className="text-sm text-slate-600">
                    I agree to the <Link to="/privacy-policy" className="text-amber-500 underline">Privacy Policy</Link>.<span className="text-red-500">*</span>
                  </label>
                </div>
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    Sorry, there was an error. Please try again.
                  </div>
                )}
                <button
                  type="submit" disabled={isSubmitting || !agreePrivacy}
                  className="w-full px-6 py-4 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Book a Free Consultation Today'}
                </button>
              </form>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-1"></div>
        </div>
      </div>
    </section>
  );
}
